import * as _ from 'lodash-es';

import { Injectable } from '@angular/core';
import { UserRest } from './core.user.rest.service';
import { ProjectService } from './project.service';
import { HttpClient } from '@angular/common/http';
import { PermissionsService } from './core.permissions.service';
import { GfTranslateService } from './core.translate.service';
import { RefusedError } from './core.refused-error';
import { UiService } from './core.ui.service';
import { UserService } from './core.user.service';
import { doGetPromise } from '../config/core.httpclient-utils';

/*
    * Model Object: ProjectModel
    *
    * This service object provides a model for project, holds the project properties (fields),
    * error fields (that result after a POST, PUT, DELETE operation
*/

@Injectable()
export class ProjectModel {

    private _project = null;
    private permissionCache = {};

    constructor(
        private userRest: UserRest,
        private projectService: ProjectService,
        private $http: HttpClient,
        private permissionsService: PermissionsService,
        private gfTranslateService: GfTranslateService,
        private uiService: UiService,
        private userService: UserService
    ) { }

    section: string = 'project';
    /**
     * @TODO: this whole section needs to be moved elsewhere, or else we won't be able to translate it
     * @type {{unixName: string, name: string, description: string, invites: string, templateProjectId: string}}
     */
    template = {
        unixName: '',
        name: '',
        description: '',
        modules: ['tracker', 'scmgit', 'forum', 'wiki', 'docman', 'build', 'frs'],
        invites: '',
        templateProjectId: 0,
        isPublic: 'N'
    };

    updating = false;

    status = {
        pending: {
            label: this.gfTranslateService.translate('Pending'),
            description: this.gfTranslateService.translate('This project is still pending approval')
        },
        active: {
            label: this.gfTranslateService.translate('Active'),
            description: ''
        },
        onhold: {
            label: this.gfTranslateService.translate('On Hold'),
            description: ''
        }
    };

    visibility = {
        public: {
            label: this.gfTranslateService.translate('Public'),
            description: this.gfTranslateService.translate('This project is visible to the public'),
        },
        private: {
            label: this.gfTranslateService.translate('Private'),
            description: this.gfTranslateService.translate('This is a private project, only accessible to members')
        }
    };

    errorTypes = {
        unixName_Database: this.gfTranslateService.translate('A project with the same unix name already exists.'),
        length: this.gfTranslateService.translate('Bad length.'),
        required: this.gfTranslateService.translate('Required field.'),
        Invalid: this.gfTranslateService.translate('Use lowercase letters, numbers or underscore.'),
        Numeric: this.gfTranslateService.translate('Numeric names not allowed.'),
        Exists: this.gfTranslateService.translate('Already registered.'),
        'Not Found': this.gfTranslateService.translate('Project not found.'),
        Restricted: this.gfTranslateService.translate('Updating name is restricted.')
    };

    accessLevels = {
        onOff: {
            'On': 1,
            'Off': 0
        },

        trackerAccess: {
            'None': 0,
            'Read/Add': 5,
            'Assignee': 10,
            'Assigner': 20,
            'Admin': 99,
            'Member': 1
        },

        trackerAdmin: {
            'None': 0,
            'Admin': 99
        },

        scmAccess: {
            'None': -1,
            'Read': 0,
            'Write': 1
        },

        forumAdmin: {
            'On': 2,
            'Off': -1
        },

        forumAccess: {
            'None': -1,
            'Read': 0,
            'Post': 1,
            'Admin': 2
        },

        wikiAccess: {
            'None': 0,
            'Read': 1,
            'Edit': 2,
            'Admin': 3
        },

        folderAccess: {
            'None': 0,
            'Read': 1,
            'Edit': 2,
            'Admin': 3
        },

        buildAccess: {
            'None': -1,
            'Read': 0,
            'Write': 1,
            'Admin': 3
        },

        packageAccess: {
            'None': 0,
            'Read': 1,
            'Admin': 2
        },
    };

    data: any = {};

    errors: any = {};

    public isOrg(project) {
        return (_.get(project, 'rel.children.length', 0) > 0 ||
            _.get(project, 'projectText6', '') == 'org');
    }

    public init() {
        Object.assign(this.data, this.template);
        this.resetErrors();
    }

    public resetErrors() {
        Object.assign(this.errors, this.template);
    }

    public moduleEnabled(module) {
        return _.indexOf(
            _.get(this.data, 'modules', []),
            module
        ) > -1;
    }

    public originalModuleEnabled(module) {
        return _.indexOf(
            _.get(this.data, 'originalModules', []),
            module
        ) > -1;
    }

    public scmEnabled() {
        return _.filter(
            _.get(this.data, 'originalModules', []),
            function (mod) {
                return _.startsWith(mod, 'scm');
            }
        ).length > 0;
    }

    public getAvailableScmModules() {
        return doGetPromise(
            this.$http,
            '/api/scm/config/available'
        );
    }

    public getShowStandups(projectId) {
        return doGetPromise(
            this.$http,
            `/api/setting/project/${projectId}/showStandups?defaultValue=1`
        );
    }

    public inviteList() {
        return _.split(
            _.get(this.data, 'invites', ''),
            ','
        );
    }

    public isProjectMember(userUnixName, projectUnixName) {
        return new Promise(
            (resolve, reject) => {
                this.getPermissions(userUnixName, projectUnixName)
                    .then(
                        (response: any) => resolve(
                            _.size(response) > 0
                        ),
                        () => reject()
                    );
            }
        );
    }

    /**
     * Returns a promise that resolves or rejects based on the value of the flag parameter.
     */
    public assertPermission(flagValue) {
        return new Promise(
            (resolve, reject) => {
                if (flagValue) {
                    resolve(true);
                } else {
                    const refused = RefusedError.forbidden(
                        this.gfTranslateService.translate('Not Authorized')
                    );
                    refused.data = refused.message;
                    reject(refused);
                }
            }
        );
    }

    /**
     * Basically a promise wrapper for hasPermission.
     * This method is for use in resolves, checking permissions before navigation.
     */
    public checkPermission(project, section) {
        return this.assertPermission(
            this.hasPermission(project, section)
        );
    }

    /**
     * Wrapper for getPermissionForSection, that sets the current project before calling.
     */
    public hasPermission(project, section) {
        if (_.isEmpty(project)) {
            return false;
        } else {
            this.setCurrentProject(project);
            return this.getPermissionForSection(section);
        }
    }

    /**
     * Determine if the user has any permission in the section, for the current project.
     * See also setCurrentProject method.
     * Returns true or false, depending on the permission value and the section being checked.
     */
    public getPermissionForSection(section) {
        var value = _.get(this._project, 'rel.permissions.' + section, -1);
        return this.permissionsService.read(section, value);
    }

    /**
     * Returns the SCM identifier that is enabled on the project (if any)
     * 
     * @param {object} project 
     * @returns {string} Either scmgit, scmsvn, scmsvs or null
     */
    public getEnabledScm(project) {
        return _.find(_.get(project, 'rel.plugins', []), function (item) {
            return (item == 'scmgit' || item == 'scmsvn' || item == 'scmcvs');
        });
    }

    /**
     * Determine if the user has tracker admin permissions in the current project
     * Returns true or false, depending on the permission value
     */
    public isTrackerAdmin() {
        var value = _.get(this._project, 'rel.permissions.tracker', -1);
        return (value >= this.accessLevels.trackerAdmin.Admin);
    }

    public getTrackers() {
        var trackers = _.get(this._project, 'rel.trackers', []);

        if (this.isTrackerAdmin) {
            return trackers;
        } else {
            return _.filter(trackers, function (tracker) {
                return (tracker.access_level > 0);
            });
        }
    }

    public async getProjectPermissions(projectUnixName): Promise<any> {
        if (!_.has(this.permissionCache, projectUnixName)) {
            this.permissionCache[projectUnixName] = await this.projectService.details(projectUnixName, { rel: 'permissions' });
        }
        return Promise.resolve(_.get(this.permissionCache, projectUnixName));
    }

    public getPermissions(userUnixName, projectUnixName) {
        //Key to idenfity the request
        const key = this.section + '-' + projectUnixName + '-' + userUnixName;

        return new Promise(
            (resolve, reject) => {
                if (!this.userService.session.hasUser()) {
                    resolve([]);
                }

                this.userRest.getPermissions(userUnixName, { project: projectUnixName })
                    .then(
                        (response: any) => {
                            if (response && response.items) {
                                resolve(response.items);
                            } else {
                                reject();
                            }
                        },
                        () => reject()
                    );
            }
        );
    }

    public setCurrentProject(project) {
            this._project = project;
    }

    public getCurrentProject() {
        if (!_.isEmpty(this._project)) {
            return this._project;
        }
        return null;
    }

    public getCurrentAccessLevels(availableSettings, currentSettings) {
        var ret = [];
        var maxValues = {};

        for (var i in currentSettings) {
            if (maxValues[currentSettings[i].refId]) {
                //Order is not guaranteed so if we get the current setting value as greater than our previous, take the new one
                //Additionally, if the user has none permission, the value for member fields will be shown as equal(0) so we need
                //to default to the setting record that actually corresponds to none, found by relatedId being null
                if (currentSettings[i].value > maxValues[currentSettings[i].refId].value || (currentSettings[i].value == 0 &&
                    maxValues[currentSettings[i].refId].value == 0 && !currentSettings[i].relatedId)) {
                    maxValues[currentSettings[i].refId] = currentSettings[i];
                }
            } else {
                maxValues[currentSettings[i].refId] = currentSettings[i];
            }
        }

        //Contents of maxValues are our current settings. Store them as {value:*item*} to allow ngModel binding to update properly
        for (var id in maxValues) {
            //get the specific object from the trackerAccessLevels array so the select works
            var item = _.find(availableSettings[id], function (item) {
                return maxValues[id].value == item.value && maxValues[id].relatedId == item.relatedId
            });
            ret.push({ value: item });
        }

        return ret;
    }

    public getTrackerAccessLevels(settings) {
        var accessLevels = {};

        var createClone = function (setting, value, relatedId) {
            var clone = _.cloneDeep(setting);
            clone.value = value;
            clone.relatedId = relatedId;
            return clone;
        }

        for (var i in settings) {
            if (accessLevels[settings[i].refId]) {
                //Build up member permissions options as we find them
                if (settings[i].relatedId) {
                    var clone = createClone(settings[i], 1, settings[i].relatedId);
                    accessLevels[settings[i].refId]['Member - ' + settings[i].relatedName] = clone;
                }
            } else {
                accessLevels[settings[i].refId] = {
                    'None': createClone(settings[i], 0, null),
                    'Read/Add': createClone(settings[i], 5, null),
                    'Assignee': createClone(settings[i], 10, null),
                    'Assigner': createClone(settings[i], 20, null),
                    'Admin': createClone(settings[i], 99, null)
                }
                //Build up member permissions options as we find them
                if (settings[i].relatedId) {
                    var clone = createClone(settings[i], 1, settings[i].relatedId);
                    accessLevels[settings[i].refId]['Member - ' + settings[i].relatedName] = clone;
                }
            }
        }

        return accessLevels;
    }

    public loadProjectRoles(unixName) {
        return new Promise(
            (resolve, reject) => {
                this.projectService.roles(unixName)
                    .then(
                        (response: any) => {
                            resolve(response.items);
                        },
                        () => {
                            const msg = this.gfTranslateService.translate(
                                'There was a problem loading project roles: {{unixName}}',
                                { unixName: unixName });
                            this.uiService.message.showWarning(
                                msg,
                                this.gfTranslateService.translate('Data Error')
                            );
                            reject();
                        }
                    );
            }
        );
    }

    public getStatusDescription(project: any) {
        const status = project?.status ?? null;
        
        if (status == 0) {
            return this.gfTranslateService.translate('Pending');
        } else if (status == 1) {
            return this.gfTranslateService.translate('Active');
        } else if (status == 3) {
            return this.gfTranslateService.translate('Holding');
        } else {
            return this.gfTranslateService.translate('Unknown');
        }
    }
}
